import { FC, useContext, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  H2,
  InfoBlock,
  Modal,
  NoFinishIcon,
  PasswordInput,
  Preloader,
  TextArea,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from 'auth';
import { CurrentClubCard } from 'auth/CurrentClubCard';
import { PrintWorkShiftReport } from 'auth/EndWorkShiftModal/PrintWorkShiftReport';
import { FINISH_WORK_SHIFT, FORCE_FINISH_WORK_SHIT, GET_ME } from 'auth/schema';
import { CashboxContext, useIsCashboxEnabled } from 'cashbox';
import { LocalizationContext } from 'localization';
import { GET_WORKSHIFT_MONEY } from 'schemas';
import { SettingsContext } from 'settings';
import { EPaymentMethod, EPaymentStatus } from 'types';

import s from './EndWorkShiftModal.module.scss';
import {
  ESteps,
  FormEndWorkShiftModalValues,
  IEndWorkShiftFormProps,
  IGetWorkShiftDataResponse,
} from './interfaces';
import { GET_WORKSHIFT_DATA } from './schema';

const formatSum = (sum: number): string =>
  sum.toLocaleString(undefined, { maximumFractionDigits: 2 });

export const EndWorkShiftModal: FC<IEndWorkShiftFormProps> = ({ onClose }) => {
  const [step, setStep] = useState<ESteps>(ESteps.DETAILS);
  const [canCloseWorkShift, setCanCloseWorkShift] = useState(false);
  const isCashboxEnabled = useIsCashboxEnabled();
  const {
    closeWorkShift: cashboxCloseWorkShift,
    printXReport,
    loadKKMs,
  } = useContext(CashboxContext);
  const {
    state: {
      settings: { shouldPrintReport, currency },
    },
    actions: { loadSettings },
  } = useContext(SettingsContext);

  const {
    data: { permissions, isActiveOperator },
    actions: { logout },
  } = useContext(AuthContext);

  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { isSubmitting },
  } = useForm<FormEndWorkShiftModalValues>({ mode: 'onChange' });

  const [closeWorkShift, { called: calledCloseWS }] = useMutation(
    FINISH_WORK_SHIFT,
    {
      onCompleted: () => {
        if (isCashboxEnabled) {
          cashboxCloseWorkShift();
        }

        onClose();
        logout();
      },
      onError: (error) => {
        if (error.message === 'invalid password') {
          setError('password', { message: t('errors.password.incorrect') });
        }

        if (error.message === 'work shift not found') {
          setStep(ESteps.CLOSED);
        }
      },
    }
  );

  const [forceCloseShift, { called: calledForceCloseWS }] = useMutation(
    FORCE_FINISH_WORK_SHIT,
    {
      refetchQueries: [GET_ME, GET_WORKSHIFT_MONEY],
      onError: (error) => {
        if (error.message === 'work shift not found') {
          setStep(ESteps.CLOSED);
        }
      },
      onCompleted: onClose,
    }
  );

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (permissions?.has('worker_manage')) {
      setCanCloseWorkShift(true);
    }
  }, [permissions]);

  useEffect(() => {
    setCanCloseWorkShift(!shouldPrintReport);
  }, [shouldPrintReport]);

  const { t } = useContext(LocalizationContext);

  const [reportRest, setReportRest] = useState<number>(0);

  const {
    data: paymentsResponse,
    loading,
    error: workshiftError,
  } = useQuery<IGetWorkShiftDataResponse>(GET_WORKSHIFT_DATA, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (calledForceCloseWS || calledCloseWS) return;

    if (workshiftError?.message === 'work shift not found') {
      setStep(ESteps.CLOSED);
    }
  }, [workshiftError?.message]);

  useEffect(() => {
    if (!paymentsResponse || !paymentsResponse.activeWorkShift) return;

    const notClosePostPayment = paymentsResponse.activeWorkShift.payments
      .filter(({ method }) => method === EPaymentMethod.POSTPAID)
      .some(({ status }) => status === EPaymentStatus.PENDING);

    if (notClosePostPayment) {
      setStep(ESteps.POST_PAYMENTS);
    }
  }, [paymentsResponse?.activeWorkShift]);

  const {
    cash_on_start = 0,
    cash_orders = [],
    cash = 0,
    card = 0,
    total = 0,
  } = paymentsResponse?.getWorkShiftPaymentOverviewData ?? {};

  const cashOrdersDiff =
    cash_orders.reduce((acc, order) => {
      if (order.type === 'PKO') {
        return acc + order.sum;
      }

      if (order.type === 'RKO') {
        return acc - order.sum;
      }

      return acc;
    }, 0) ?? 0;

  const inCashBox = cash_on_start + cash + cashOrdersDiff;

  return (
    <Modal
      onClose={onClose}
      className={cn(
        s.container,
        step === ESteps.POST_PAYMENTS && s.container_postPayment
      )}
      disableOutsideClose
      disableCloseByEsc
      rootId="modal-root"
    >
      <Preloader loading={loading}>
        {step !== ESteps.POST_PAYMENTS && (
          <header className={s.header}>
            {t(
              step === ESteps.CLOSED
                ? 'workShifts.finish.finished.title'
                : 'workShifts.finish.title'
            )}
          </header>
        )}
        {step === ESteps.DETAILS && (
          <>
            <div className={s.summary}>
              <CurrentClubCard />
              <div className={s.money}>
                <div className={s.proceeds}>
                  <div className={s.block}>
                    <span className={s.title}>{t('money.proceeds')}</span>
                    <span className={cn(s.sum, s.big)}>
                      {formatSum(total)} {currency.sign}
                    </span>
                  </div>
                  <div className={s.separated}>
                    <div className={s.block}>
                      <span className={s.title}>{t('money.cash')}</span>
                      <span className={s.sum}>
                        {formatSum(cash)} {currency.sign}
                      </span>
                    </div>
                    <div className={s.block}>
                      <span className={s.title}>{t('money.card')}</span>
                      <span className={s.sum}>
                        {formatSum(card)} {currency.sign}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={s.cashbox}>
                  <div className={s.block}>
                    <span className={s.title}>{t('money.inCashBox')}</span>
                    <span className={cn(s.sum, s.big)}>
                      {formatSum(inCashBox)} {currency.sign}
                    </span>
                  </div>
                  <div className={s.block}>
                    <span className={s.title}>{t('money.atStart')}</span>
                    <span className={s.sum}>
                      {formatSum(cash_on_start)} {currency.sign}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className={s['next-container']}>
              <Button onClick={() => setStep(1)} isLoading={loading} fullWidth>
                {t('next')}
              </Button>
              {isCashboxEnabled &&
                (reportRest ? (
                  <span className={s['report-rest']}>
                    {t('workShifts.finish.wait', reportRest)}
                  </span>
                ) : (
                  <Button
                    onClick={() => {
                      const intervalId = setInterval(
                        () => setReportRest((value) => value - 1),
                        1000
                      );

                      printXReport().finally(() => {
                        clearInterval(intervalId);

                        setReportRest(0);
                      });
                    }}
                    fullWidth
                    variant="ghost"
                    className={s['x-report']}
                  >
                    {t('workShifts.reports.X')}
                  </Button>
                ))}
            </div>
          </>
        )}
        {step === ESteps.CONFIRM && (
          <form
            className={s.form}
            onSubmit={handleSubmit(({ password, comment }) => {
              if (isActiveOperator && permissions?.has('work_shift_close')) {
                if (isCashboxEnabled) {
                  return loadKKMs().then(() => {
                    return closeWorkShift({
                      variables: {
                        input: { comment: comment || null },
                        confirm_pass: password,
                      },
                    });
                  });
                }

                return closeWorkShift({
                  variables: {
                    input: { comment: comment || null },
                    confirm_pass: password,
                  },
                });
              }

              if (permissions?.has('worker_manage')) {
                return forceCloseShift({
                  variables: {
                    input: { comment: comment || null },
                  },
                });
              }

              return;
            })}
          >
            {!isActiveOperator && isCashboxEnabled && (
              <InfoBlock
                title={t('workShifts.finish.byManager.notification.title')}
                message={t(
                  'workShifts.finish.byManager.notification.description'
                )}
              />
            )}
            {isActiveOperator && (
              <span className={s.description}>
                {t('workShifts.finish.password.title')}
              </span>
            )}
            <div className={s.fields}>
              {isActiveOperator && (
                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <PasswordInput
                      {...field}
                      error={error?.message}
                      title={t('password')}
                    />
                  )}
                  name="password"
                  control={control}
                  defaultValue=""
                  rules={{ required: t('errors.required') }}
                />
              )}
              <Controller
                render={({ field, fieldState: { error } }) => (
                  <TextArea
                    {...field}
                    title={t('comment')}
                    rows={permissions?.has('worker_manage') ? 8 : 4}
                    placeholder={t('workShifts.finish.comment.placeholder')}
                    error={error?.message}
                  />
                )}
                name="comment"
                control={control}
                defaultValue=""
                rules={{
                  maxLength: {
                    value: 255,
                    message: t('errors.maxLength', 255),
                  },
                }}
              />
            </div>
            <div className={s.controls}>
              <PrintWorkShiftReport
                onAfterPrint={() => setCanCloseWorkShift(true)}
                comment={watch('comment')}
              />
              <Button
                className={s.field}
                type="submit"
                fullWidth
                isLoading={isSubmitting}
                disabled={!canCloseWorkShift}
              >
                {t('workShifts.long.submit')}
              </Button>
            </div>
          </form>
        )}
        {step === ESteps.CLOSED && (
          <Button fullWidth onClick={onClose}>
            {t('ok')}
          </Button>
        )}
        {step === ESteps.POST_PAYMENTS && (
          <>
            <NoFinishIcon size="xxl" color="secondary" />
            <H2 className={s.postPayment__title}>
              {t('workShifts.finish.with_active_post_payments')}
            </H2>
            <Button
              fullWidth
              onClick={() => {
                navigate('/payments?type=POST_PAYMENTS');
                onClose();
              }}
            >
              {t('workShifts.finish.with_active_post_payments.button')}
            </Button>
          </>
        )}
      </Preloader>
    </Modal>
  );
};
